<template>
    <VcForm
        ref="editWidgetForm"
        :model="model"
        :rules="rules"
        :show-message="false"
        class="widget-edit-form"
    >
        <VcCollapse v-model="activeCollapse">
            <RedesignFormItem
                :label="t('widget.title')"
                :rules="[required]"
                prop="Title"
            >
                <VcInput v-model="model.Title" />
            </RedesignFormItem>
            <RedesignFormItem
                v-if="isWidgetRefreshInterval"
                :label="t('widget.widgetRefreshInterval')"
                :info-text="t('widget.setIntervalOnWidgetReload')"
            >
                <RedesignWidgetRefreshInterval
                    v-model="model.WidgetLayout.DefaultRefreshInterval"
                    :min-value="minIntervalValue"
                    :max-value="maxIntervalValue"
                />
            </RedesignFormItem>
            <div v-if="TABLE_TEMPLATE_LIST.includes(Number(model?.TemplateID))">
                <VcCollapseItem
                    :title="t('settings.sorting')"
                    name="sorting"
                    class="simple-collapse"
                >
                    <RedesignSortingParams
                        v-model="model.WidgetLayout.sortingParams"
                        :template-columns="model.WidgetTemplateColumns"
                    />
                </VcCollapseItem>
            </div>
            <RedesignFormItem v-if="isMultiQueuesDashboard(model)">
                <div class="flex w-full flex-col gap-2">
                    <div class="flex">
                        <VcCheckbox
                            :model-value="model.WidgetLayout.showStatsInPercentage ?? false"
                            @change="model.WidgetLayout.showStatsInPercentage = $event"
                        >
                            {{ t('widget.showStatsInPercentage') }}
                        </VcCheckbox>
                    </div>
                    <div
                        v-if="canToggleDisplayMode"
                        class="flex"
                    >
                        <VcCheckbox
                            v-model="displayQueuesAsRow"
                        >
                            {{ t('widget.config.displayQueuesAsRows') }}
                        </VcCheckbox>
                    </div>
                    <div class="flex">
                        <VcCheckbox
                            :model-value="model.WidgetLayout.displayRowWithTotals ?? true"
                            @change="model.WidgetLayout.displayRowWithTotals = $event"
                        >
                            {{ t('widget.displayTheRowWithTotals') }}
                        </VcCheckbox>
                    </div>
                </div>
            </RedesignFormItem>
            <template v-if="isQueueGauge(model)">
                <RedesignFormItem
                    :label="t('widget.config.warningRangeValue')"
                >
                    <VcInputNumber
                        v-model="warningLayoutRange"
                        :max-value="maximumLayoutRange - 1"
                        :min-value="0"
                        :step="1"
                    />
                </RedesignFormItem>
                <RedesignFormItem
                    :label="t('widget.config.maximumRangeValue')"
                >
                    <VcInputNumber
                        v-model="maximumLayoutRange"
                        :max-value="1000"
                        :min-value="0"
                        :step="2"
                    />
                </RedesignFormItem>
                <RedesignFormItem>
                    <VcCheckbox
                        :model-value="model.WidgetLayout.showWaitingTimeTable ?? false"
                        @change="model.WidgetLayout.showWaitingTimeTable = $event"
                    >
                        {{ t('widget.showWaitingTimeTable') }}
                    </VcCheckbox>
                </RedesignFormItem>
            </template>
            <RedesignFormItem v-if="isFunnelChartWidget(model)">
                <VcCheckbox
                    :model-value="model.WidgetLayout.showAutoDialerTable ?? false"
                    @change="model.WidgetLayout.showAutoDialerTable = $event"
                >
                    {{ t('widget.showAutoDialerTable') }}
                </VcCheckbox>
                <VcCheckbox
                    :model-value="model.WidgetLayout.displayTableAsRow ?? false"
                    @change="model.WidgetLayout.displayTableAsRow = $event"
                >
                    {{ t('widget.config.displayTableAsRow') }}
                </VcCheckbox>
                <VcCheckbox
                    :model-value="model.WidgetLayout.rotateChartHorizontally ?? false"
                    @change="model.WidgetLayout.rotateChartHorizontally = $event"
                >
                    {{ t('widget.config.rotateChartHorizontally') }}
                </VcCheckbox>
            </RedesignFormItem>
            <div v-if="isQueueChart(model)">
                <RedesignFormItem
                    :label="t('queues.to.display')"
                >
                    <VcSelect
                        v-model="model.WidgetLayout.showQueues"
                        :options="allQueues"
                        :config="{
                            labelKey: 'QueueName',
                            valueKey: 'QueueID'
                        }"
                        multiple="true"
                        filterable
                    />
                </RedesignFormItem>
                <RedesignFormItem
                    :label="t('blocks.to.display')"
                >
                    <VcSelect
                        v-model="model.WidgetLayout.showSeries"
                        :options="allSeries"
                        :config="{
                            labelKey: 'label',
                            valueKey: 'value'
                        }"
                        multiple="true"
                        filterable
                    />
                </RedesignFormItem>
                <div class="mb-5">
                    <VcCheckbox
                        :value="queueChartShowLogout"
                        @update:checked="queueChartToggleLogoutSerie"
                    >
                        {{ t('settings.extension.logout') }}
                    </VcCheckbox>
                </div>
            </div>
            <RedesignFormItem
                v-if="isQueueDashboardWidget(model)"
                :label="t('statistics.to.display')"
            >
                <VcSelect
                    v-model="model.WidgetLayout.ShowStatistics"
                    :options="queueDashboardStatisticOptions"
                    :config="{
                        labelKey: 'key',
                        valueKey: 'key'
                    }"
                    :multiple="true"
                    :teleported="false"
                    filterable
                />
                <div class="flex w-full flex-col gap-3 py-3">
                    <div class="flex">
                        <VcCheckbox
                            :model-value="model.WidgetLayout.SumOfOthers ?? false"
                            @change="model.WidgetLayout.SumOfOthers = $event"
                        >
                            {{ t('widget.displayPercentOfOthersValue') }}
                        </VcCheckbox>
                    </div>
                    <div class="flex">
                        <VcCheckbox
                            :model-value="model.WidgetLayout.AbsoluteNumbers ?? false"
                            @change="model.WidgetLayout.AbsoluteNumbers = $event"
                        >
                            {{ t('widget.displayAbsoluteNumbers') }}
                        </VcCheckbox>
                    </div>
                    <div class="flex">
                        <VcCheckbox
                            :model-value="model.WidgetLayout.stretchToWidgetSize ?? false"
                            @change="model.WidgetLayout.stretchToWidgetSize = $event"
                        >
                            {{ t('widget.stretchToWidgetSize') }}
                        </VcCheckbox>
                    </div>
                </div>
                <div class="mb-3 w-full">
                    <div class="text-sm mb-1">
                        {{ t('widget.innerAlignment') }}
                    </div>
                    <VcRadioButtonsGroup
                        v-model="cardTextAlign"
                        class="radio-groups"
                        :radios="TEXT_ALIGN_OPTIONS"
                        inline
                        size="small"
                        :config="{
                            valueKey: 'value',
                            labelKey: 'icon',
                            iconKey: 'icon'
                        }"
                    >
                        <template #item="{ option }">
                            <RedesignBaseIcon
                                :name="option.icon"
                                :size="20"
                            />
                        </template>
                    </VcRadioButtonsGroup>
                </div>
                <QueueDashboardReorderModal
                    :visible="reorderQueueDashboard"
                    :model-value="queueDashboardConfig"
                    :active-keys="model.WidgetLayout.ShowStatistics || []"
                    @close="reorderQueueDashboard = false"
                    @update:modelValue="updateQueueDashboardConfig"
                    @cancel="cancelQueueDashBoardReorder"
                />
                <VcButton
                    icon="arrow-up-down"
                    @click="openReorderQueueDashboard"
                >
                    <span>{{ t('common.reorder') }}</span>
                </VcButton>
            </RedesignFormItem>
            <div v-if="isQueueActivityGauge(model)">
                <VcCollapseItem
                    :title="t('widget.config')"
                    name="queueActivity"
                    class="simple-collapse"
                    :has-border="false"
                >
                    <RedesignActivityGaugeConfig
                        v-model="model.WidgetLayout"
                    />
                </VcCollapseItem>
            </div>
            <div>
                <VcCollapseItem
                    :title="t('widget.layout')"
                    class="simple-collapse !pb-0"
                    name="layout"
                    :has-border="false"
                >
                    <RedesignWidgetLayoutConfig
                        v-model="model.WidgetLayout"
                        :available-colors="availableColors"
                        :is-html-editor="isHtmlEditor(model)"
                        :is-note-list-widget="isNoteListWidget(model)"
                        :is-queue-dashboard-widget="isQueueDashboardWidget(model)"
                        :is-clock-and-date-widget="isClockAndDateWidget(model)"
                        :is-queue-gauge="isQueueGauge(model)"
                        :is-pie-widget="isPieWidget(model)"
                        :is-table-widget="isTableWidget(model)"
                        :is-funnel-chart-widget="isFunnelChartWidget(model)"
                        :is-chart-widget="isChartWidget(model)"
                        :is-extension-card-statistics-widget="isExtensionCardStatisticsWidget(model)"
                    />
                </VcCollapseItem>
            </div>
            <div
                v-if="isMultiQueuesDashboard(model)"
                class="mb-5"
            >
                <VcCollapseItem
                    :title="t('settings.sounds')"
                    name="sounds"
                    class="simple-collapse"
                    :has-border="false"
                >
                    <WidgetSoundsSettings
                        v-model="model.WidgetLayout"
                    />
                </VcCollapseItem>
            </div>
            <template v-if="showTimeFrame">
                <div v-if="model.WidgetTime?.type">
                    <VcCollapseItem
                        :title="t('settings.time.frame')"
                        name="widget-time-frame"
                        class="simple-collapse"
                        :has-border="false"
                    >
                        <RedesignWidgetTimeFrame
                            v-model="model.WidgetTime"
                            :time-frame-type="model.WidgetTime?.type"
                            :widget-time-options="widgetTimeOptions"
                        >
                            <template #frame-types>
                                <VcRadioButtonsGroup
                                    v-model="model.WidgetTime.type"
                                    :radios="createWidgetTimeTypes"
                                    :config="{ valueKey: 'value', labelKey: 'label' }"
                                    class="radio-groups mb-4"
                                />
                            </template>
                        </RedesignWidgetTimeFrame>
                    </VcCollapseItem>
                </div>
            </template>
            <div
                v-if="isRealtimeWidget(model) && widgetSettings"
                class="mb-5"
            >
                <VcCollapseItem
                    :title="t('settings.threshold')"
                    :has-border="false"
                    name="threshold"
                    class="simple-collapse"
                >
                    <WidgetRealTimeSettings
                        :model-value="widgetSettings"
                        :data-type="model.DataTypeID"
                        class="mb-5"
                        @update:modelValue="updateWidgetSettings"
                    />
                </VcCollapseItem>
            </div>
            <div
                v-if="widgetConfig.filters.length">
                <VcCollapseItem
                    :title="t('settings.filters')"
                    name="filters"
                    class="simple-collapse"
                    :has-border="false"
                >
                    <div
                        v-if="autoCompletes.length"
                        class="flex flex-col text-main-sm items-start main-text-color gap-2 mb-5"
                    >
                        {{ t('tooltip.refresh.entities.list') }}
                        <RefreshButton
                            :disabled="loadEntitiesList"
                            :loading="loadEntitiesList"
                            @click="refreshEntitiesList"
                        />
                    </div>
                    <div
                        v-for="(filter, index) in widgetConfig.filters"
                        :key="index"
                    >
                        <RedesignWidgetConfigEntityTree
                            v-if="isAutoComplete(filter)"
                            :key="`filter-tree-${filter.ParameterID}`"
                            :value="getWidgetConfig(filter)"
                            :ordered-list="model.WidgetLayout?.sortingParams?.defaultList"
                            :show-order-component="isTableWidget(model)"
                            class="mb-4"
                            @update:value="updateFilterItem"
                            @update:sortingParams="handleSortingParamsUpdate"
                        />
                        <RedesignWidgetConfigAutoComplete
                            v-if="isAutoCompleteSelect(filter)"
                            :key="`filter-auto-complete-${filter.ParameterID}`"
                            :value="getWidgetConfig(filter)"
                            class="mb-4"
                            @update:value="updateFilterItem"
                        />
                        <RedesignWidgetConfigSelectExtended
                            v-if="isSelectExtended(filter)"
                            :key="`filter-select-extended-${filter.ParameterID}`"
                            :value="getWidgetConfig(filter)"
                            class="mb-4"
                            @update:value="updateFilterItem"
                        />
                        <RedesignWidgetConfigSelect
                            v-if="isMultiSelect(filter)"
                            :key="`filter-select-${filter.ParameterID}`"
                            :value="getWidgetConfig(filter)"
                            @update:value="updateFilterItem"
                        />
                        <VcCheckbox
                            v-if="isAutoComplete(filter) && (Number(model.TemplateID) === 75)"
                            :model-value="model.WidgetLayout.hideLoggedOff ?? false"
                            @change="model.WidgetLayout.hideLoggedOff = $event"
                        >
                            <span class="main-text-color">
                                {{ t('widget.hideLoggedOff') }}
                            </span>
                        </VcCheckbox>
                    </div>
                </VcCollapseItem>
            </div>
            <div v-if="widgetConfig.otherFilters.length">
                <VcCollapseItem
                    :title="t('settings.other.filters')"
                    name="otherFilters"
                    class="simple-collapse"
                    :has-border="false"
                >
                    <div
                        v-for="(filter, index) in widgetConfig.otherFilters"
                        :key="`other-filter-${index}`"
                        class="mb-4"
                    >
                        <RedesignWidgetOtherFilters
                            :value="getWidgetConfig(filter)"
                            :parameter-type="filter.ParameterType"
                            @update:value="updateFilterItem"
                        />
                    </div>
                </VcCollapseItem>
            </div>
            <div>
                <RedesignFormItem v-if="isQueueTable(model)">
                    <VcCheckbox v-model="hideVirtualHoldCalls">
                        {{ t('widget.settings.queue.activeCalls.hideVirtual') }}
                    </VcCheckbox>
                </RedesignFormItem>
            </div>
            <RedesignFormItem v-if="isClockAndDateWidget(model)">
                <div class="flex w-full flex-col gap-3">
                    <div class="flex">
                        <VcCheckbox
                            :model-value="model.WidgetLayout.show_date ?? false"
                            @change="model.WidgetLayout.show_date = $event"
                        >
                            {{ t('widget.showDate') }}
                        </VcCheckbox>
                    </div>
                    <div class="flex">
                        <VcCheckbox
                            :model-value="model.WidgetLayout.show_time ?? false"
                            @change="model.WidgetLayout.show_time = $event"
                        >
                            {{ t('widget.showTime') }}
                        </VcCheckbox>
                    </div>
                    <RedesignFormItem
                        v-if="model.WidgetLayout.show_time"
                        :label="t('widget.clock.time.format')"
                    >
                        <RedesignCustomSelect
                            :value="model.WidgetLayout?.timeFormat"
                            :options="timeFormatOptions"
                            :default-value="DEFAULT_TIME_FORMAT"
                            :custom-input-label="t('widget.clock.time.format.customInput')"
                            :custom-input-placeholder="t('widget.clock.time.format.customPlaceholder')"
                            @update:value="handleTimeFormatUpdate"
                        />
                    </RedesignFormItem>
                    <RedesignFormItem
                        v-if="model.WidgetLayout.show_date"
                        :label="t('widget.clock.date.format')"
                    >
                        <RedesignCustomSelect
                            :value="model.WidgetLayout?.dateFormat"
                            :options="dateFormatOptions"
                            :default-value="DEFAULT_DATE_FORMAT"
                            :custom-input-label="t('widget.clock.date.format.customInput')"
                            :custom-input-placeholder="t('widget.clock.date.format.customPlaceholder')"
                            @update:value="handleDateFormatUpdate"
                        />
                    </RedesignFormItem>
                </div>
            </RedesignFormItem>
            <div
                v-if="wizardConfigs.length"
                class="mt-5"
            >
                <WidgetParameterSetupCard
                    ref="widgetParameterSetupCardRef"
                    :configs="wizardConfigs"
                    update-mode
                    @update:config="updateFilterItem"
                />
            </div>
        </VcCollapse>
    </VcForm>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { widgetModelData } from '@/composables/useWidgetEditModal'
import {
    isChartWidget,
    isClockAndDateWidget,
    isExtensionCardStatisticsWidget,
    isFunnelChartWidget,
    isHtmlEditor,
    isMultiQueuesDashboard,
    isNoteListWidget,
    isPieWidget,
    isQueueActivityGauge,
    isQueueChart,
    isQueueDashboardWidget,
    isQueueGauge,
    isQueueTable,
    isRealtimeWidget,
    isTableWidget,
    shouldShowRefreshInterval,
    shouldShowTimeFrame
} from '@/helpers/widgetUtils'
import RedesignFormItem from '@/components/RedesignBaseComponents/Form/RedesignFormItem.vue'
import { realTimeWidgetRules } from '@/enum/widgetUpdateRules'
import useValidationRules from '@/composables/useValidationRules'
import RedesignWidgetRefreshInterval
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetRefreshInterval.vue'
import get from 'lodash/get'
import RedesignSortingParams from '@/components/RedesignComponents/Widgets/Common/RedesignSortingParams.vue'
import { useQueuesStore } from '@voicenter-team/events-sdk-vue-3'
import { getAllSeries } from '@/enum/queueConfigs'
import { Statuses } from '@/enum/redesignStatusTypes'
import RedesignActivityGaugeConfig from '@/components/RedesignComponents/Widgets/Common/RedesignActivityGaugeConfig.vue'
import RedesignWidgetLayoutConfig from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetLayoutConfig.vue'
import { widgetTimeOptions, widgetTimeTypes } from '@/enum/widgetTimeOptions'
import RedesignWidgetTimeFrame from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetTimeFrame.vue'
import WidgetRealTimeSettings from '@/components/RedesignComponents/Widgets/Common/WidgetRealTimeSettings.vue'
import ENUM from '@/enum/parameters'
import { QueueDashboardConfig, Settings, WidgetConfig, WidgetExtended } from '@/types/dashboard'
import RefreshButton from '@/components/RefreshButton.vue'
import RedesignWidgetConfigSelect from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigSelect.vue'
import RedesignWidgetOtherFilters from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetOtherFilters.vue'
import WidgetSoundsSettings from '@/components/RedesignComponents/Widgets/Common/WidgetSoundsSettings.vue'
import { realTimeSettings } from '@/enum/defaultWidgetSettings'
import cloneDeep from 'lodash/cloneDeep'
import { refreshEntitiesData, selectedAccountData } from '@/composables/globalData/useEntitiesData'
import {
    getWidgetConfigByTemplateId,
    getWidgetLayoutDefaultColors,
    isSelectExtended,
    isAutoComplete,
    isAutoCompleteSelect
} from '@/components/RedesignComponents/Widgets/widget.helper'
import {
    getWizardParameters,
    isWizardParameter
} from '@/components/RedesignComponents/Widgets/widgetParameterRegistry'
import { DEFAULT_MAX_RANGE, DEFAULT_WARNING_RANGE } from '@/enum/gradeGauge'
import { useI18n } from 'vue-i18n'
import RedesignWidgetConfigSelectExtended
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigSelectExtended.vue'
import { getTranslatedTimeFormats, getTranslatedDateFormats } from '@/helpers/dateTimeFormats'
import { DEFAULT_TIME_FORMAT, DEFAULT_DATE_FORMAT } from '@/enum/dateTimeFormats'
import RedesignCustomSelect from '@/components/RedesignComponents/Select/RedesignCustomSelect.vue'
import { getAllQueueStatisticArray, buildDefaultQueueDashboardConfig } from '@/enum/redesignQueueDashboardStatistics'
import {
    MULTI_QUEUE_AS_ROWS_TEMPLATE_ID,
    MULTI_QUEUE_AS_COLUMNS_TEMPLATE_ID
} from '@/enum/multiQueueTemplateIds'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import QueueDashboardReorderModal from '@/components/RedesignComponents/Modal/QueueDashboardReorderModal.vue'
import RedesignWidgetConfigEntityTree
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigEntityTree.vue'
import {
    VcForm,
    VcInput,
    VcInputNumber,
    VcRadioButtonsGroup,
    VcCollapse,
    VcCollapseItem,
    VcButton,
    VcCheckbox,
    VcSelect
} from '@voicenter-team/voicenter-ui-plus'
import RedesignWidgetConfigAutoComplete
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigAutoComplete.vue'
import WidgetParameterSetupCard
    from '@/components/RedesignComponents/Widgets/Parameters/WidgetParameterSetupCard.vue'
import { ComponentExposed } from 'vue-component-type-helpers'

const { t } = useI18n()
const { required } = useValidationRules()
const queuesStore = useQueuesStore()

const editWidgetForm = ref<InstanceType<typeof VcForm>>()
const widgetParameterSetupCardRef = ref<ComponentExposed<typeof WidgetParameterSetupCard>>()

/* Props */
const props = defineProps<{
    modelValue: WidgetExtended
}>()
/* Emits */
const emit = defineEmits<{
    (e: 'update:modelValue', val: WidgetExtended): void
}>()

/* Form Model */
const model = computed({
    get () {
        return props.modelValue
    },
    set (val) {
        emit('update:modelValue', val)
    }
})
const widgetSettings = computed(() => {
    return {
        ...realTimeSettings,
        ...model.value.WidgetLayout.settings
    }
})

/* Data */
const TEXT_ALIGN_OPTIONS = [
    {
        value: 'start',
        icon: 'AlignLeft'
    },
    {
        value: 'center',
        icon: 'AlignCenter'
    },
    {
        value: 'end',
        icon: 'AlignRight'
    }
] as const

const TABLE_TEMPLATE_LIST = [ 9, 83 ]
const loadEntitiesList = ref(false)
const activeCollapse = ref([ '' ])
const reorderQueueDashboard = ref(false)

const timeFormatOptions = getTranslatedTimeFormats()
const dateFormatOptions = getTranslatedDateFormats()

/* Computed */
function updateWidgetSettings (settings: Settings) {
    model.value = {
        ...model.value,
        WidgetLayout: {
            ...model.value.WidgetLayout,
            settings
        }
    }
}

const rules = computed(() => {
    if (isRealtimeWidget(model.value)) {
        return realTimeWidgetRules(model.value.WidgetLayout)
    }
    return {}
})
const isWidgetRefreshInterval = computed(() => {
    return (widgetModelData.value
        && 'DefaultRefreshInterval' in widgetModelData.value
        && shouldShowRefreshInterval(widgetModelData.value))
})

const showTimeFrame = computed(() => {
    return widgetModelData.value && shouldShowTimeFrame(widgetModelData.value)
})

const templateSettings = computed(() => {
    return get(widgetModelData.value, 'WidgetLayout.TemplateSettings', {
        MinimumRefreshInterval: 60,
        MaximumRefreshInterval: 1800,
    })
})
const minIntervalValue = computed(() => {
    return Number(templateSettings.value.MinimumRefreshInterval)
})
const maxIntervalValue = computed(() => {
    return Number(templateSettings.value.MaximumRefreshInterval)
})
const maximumLayoutRange = computed({
    get () {
        return model.value.WidgetLayout?.maximumRange || DEFAULT_MAX_RANGE
    },
    set (val) {
        model.value.WidgetLayout = {
            ...model.value.WidgetLayout,
            maximumRange: val
        }
    }
})

const hideVirtualHoldCalls = computed({
    get () {
        return model.value.WidgetLayout?.hideVirtualHoldCalls ?? false
    },
    set (val) {
        model.value.WidgetLayout = {
            ...model.value.WidgetLayout,
            hideVirtualHoldCalls: val
        }
    }
})

const warningLayoutRange = computed({
    get () {
        return model.value.WidgetLayout?.warningRange || DEFAULT_WARNING_RANGE
    },
    set (val) {
        model.value.WidgetLayout = {
            ...model.value.WidgetLayout,
            warningRange: val
        }
    }
})
const allQueues = computed(() => {
    return queuesStore.queuesListAll.value || []
})
const allSeries = computed(() => {
    return getAllSeries(selectedAccountData.value?.AccountBreaks)
})
const queueChartShowLogout = computed(() => {
    return !!(model.value.WidgetLayout && model.value.WidgetLayout.showSeries?.length && model.value.WidgetLayout.showSeries?.includes(Statuses.LOGOUT))
})
const createWidgetTimeTypes = computed(() => {
    return widgetTimeTypes
        .map(el => {
            return {
                label: t(el.text),
                value: el.label
            }
        })
})
const autoCompletes = computed(() => {
    return model.value.WidgetConfig.filter(config => isAutoComplete(config))
})

const displayQueuesAsRow = computed({
    get: () => model.value.WidgetLayout?.displayQueuesAsRow ?? true,
    set: (value) => {
        model.value = {
            ...model.value,
            WidgetLayout: {
                ...model.value.WidgetLayout,
                displayQueuesAsRow: value
            }
        }
    }
})

const cardTextAlign = computed({
    get: () => model.value.WidgetLayout?.cardTextAlign ?? 'center',
    set: (value: 'start' | 'center' | 'end') => {
        model.value = {
            ...model.value,
            WidgetLayout: {
                ...model.value.WidgetLayout,
                cardTextAlign: value
            }
        }
    }
})

const canToggleDisplayMode = computed(() => {
    const templateId = model.value.TemplateID
    return templateId !== MULTI_QUEUE_AS_ROWS_TEMPLATE_ID &&
        templateId !== MULTI_QUEUE_AS_COLUMNS_TEMPLATE_ID
})

const queueDashboardStatisticOptions = computed(() => {
    const customConfig = model.value.WidgetLayout.queueDashboardConfig ?? {}
    return getAllQueueStatisticArray().map(statistic => {
        return {
            key: statistic.key,
            label: customConfig[statistic.key]?.customLabel ? customConfig[statistic.key].customLabel : statistic.label
        }
    })
})

const queueDashboardConfig = computed(() =>
    model.value.WidgetLayout?.queueDashboardConfig
    ?? buildDefaultQueueDashboardConfig()
)

const wizardConfigs = computed(() => {
    return getWizardParameters(model.value.WidgetConfig ?? []).map(getWidgetConfig)
})

const widgetConfig = computed(() => {
    if (!model.value.WidgetConfig || !model.value.WidgetConfig.length) {
        return {
            filters: [],
            otherFilters: []
        }
    }
    return model.value.WidgetConfig.reduce((acc, filter) => {
        if (isWizardParameter(filter)) {
            return acc
        }

        if (isOtherFilters(filter) && filter.ParameterType) {
            acc.otherFilters.push(filter)
        } else if (!isOtherFilters(filter)) {
            acc.filters.push(filter)
        }
        return acc
    }, {
        filters: [],
        otherFilters: []
    } as { filters: Array<WidgetConfig>, otherFilters: Array<WidgetConfig> })
})


const filtersConfig = computed(() => {
    return [
        ...ENUM.AUTO_COMPLETE_TYPE_KEYS,
        ...ENUM.MULTISELECT_TYPE_KEYS,
        ...ENUM.MULTISELECT_EXTENDED
    ]
})
const availableColors = computed(() => {
    return getWidgetLayoutDefaultColors(props.modelValue) || {}
})

/* Methods */
function updateFilterItem (filterData: WidgetConfig) {
    const _index = model.value.WidgetConfig?.findIndex(i => i.ParameterID === filterData.ParameterID)

    if (_index > -1) {
        const widgetConfigToUpdate = cloneDeep(model.value.WidgetConfig)

        widgetConfigToUpdate[_index] = filterData

        model.value = {
            ...model.value,
            WidgetConfig: widgetConfigToUpdate
        }
    }
}

function isOtherFilters (config: WidgetConfig) {
    return !filtersConfig.value.includes(config.ParameterType || -1)
}

function updateQueueDashboardConfig (val: QueueDashboardConfig) {
    model.value = {
        ...model.value,
        WidgetLayout: {
            ...model.value.WidgetLayout,
            queueDashboardConfig: val
        }
    }
}
function cancelQueueDashBoardReorder () {
    reorderQueueDashboard.value = false
}

async function refreshEntitiesList () {
    loadEntitiesList.value = true
    await refreshEntitiesData()
    loadEntitiesList.value = false
}

function queueChartToggleLogoutSerie () {
    if (!('showSeries' in model.value.WidgetLayout)) {
        model.value.WidgetLayout.showSeries = []
    }
    const series = model.value.WidgetLayout.showSeries ? [ ...model.value.WidgetLayout.showSeries ] : []

    if (queueChartShowLogout.value) {
        const logoutSerieIndex = series?.findIndex(el => el === Statuses.LOGOUT) ?? -1
        if (logoutSerieIndex > -1) {
            series.splice(logoutSerieIndex, 1)
        }
    } else {
        series.push(Statuses.LOGOUT)
    }

    model.value.WidgetLayout.showSeries = series.length ? [ ...series ] : []
}
function getWidgetConfig (config: WidgetConfig) {
    return getWidgetConfigByTemplateId(config, model.value.TemplateID)
}

function isMultiSelect (config: WidgetConfig) {
    return ENUM.MULTISELECT_TYPE_KEYS.includes(config.ParameterType || -1)
}

function openReorderQueueDashboard () {
    reorderQueueDashboard.value = true
}

function handleTimeFormatUpdate (value: string | undefined) {
    const currentModel = { ...model.value }
    if (!currentModel.WidgetLayout) {
        currentModel.WidgetLayout = {}
    }
    currentModel.WidgetLayout = {
        ...currentModel.WidgetLayout,
        timeFormat: value
    }
    model.value = currentModel
}

function handleDateFormatUpdate (value: string | undefined) {
    const currentModel = { ...model.value }
    if (!currentModel.WidgetLayout) {
        currentModel.WidgetLayout = {}
    }
    currentModel.WidgetLayout = {
        ...currentModel.WidgetLayout,
        dateFormat: value
    }
    model.value = currentModel
}

function handleSortingParamsUpdate (params: { defaultList: (string | number)[], idKey: string }) {
    const currentModel = { ...model.value }

    if (!currentModel.WidgetLayout) {
        currentModel.WidgetLayout = {}
    }

    if (!currentModel.WidgetLayout.sortingParams) {
        currentModel.WidgetLayout.sortingParams = {
            prop: '',
            order: null
        }
    }

    currentModel.WidgetLayout.sortingParams = {
        ...currentModel.WidgetLayout.sortingParams,
        defaultList: params.defaultList,
        defaultProp: params.idKey,
        order: null
    }

    model.value = currentModel
}

async function validate () {
    const res = await editWidgetForm.value?.validate()

    let wizardParametersValid = true

    if (widgetParameterSetupCardRef.value) {
        wizardParametersValid = widgetParameterSetupCardRef.value.validate()

        if (wizardParametersValid) {
            widgetParameterSetupCardRef.value.clearError()
        } else {
            widgetParameterSetupCardRef.value.showError()
        }
    }

    return res?.isValid && wizardParametersValid
}


defineExpose({
    validate
})
</script>
